/**
 * Shared static regular expressions used across the application.
 * Keep only module-scope regexes here, especially:
 * - reused validation patterns
 * - large patterns
 * - patterns extracted to satisfy lint rules
 *
 * Note: a regex with the /g flag keeps its lastIndex between calls, so the same
 * constant used with .test() returns alternating results. Patterns used for
 * validation must not carry /g.
 */

export const REGEX_WHITESPACE = /\s+/g;
export const REGEX_HAS_WHITESPACE = /\s/;
export const REGEX_UNDERSCORE = /_/g;
export const REGEX_HEADING_WORD_START = /\b\w/g;
export const REGEX_STEP_SEPARATOR = /[-_]/g;
export const REGEX_TRAILING_SLASH = /\/$/;

export const REGEX_NAME_NO_DOLLAR_NO_DOT = /^(?!\$)[^.]+$/;
export const REGEX_USER_EDIT_PATH = /^\/users\/edit\/[^/]+$/;
export const REGEX_ROOT_PATH = /^\/$/;

export const REGEX_EMAIL = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const REGEX_SLACK_WEBHOOK = /https:\/\/hooks\.slack\.com\/services\/T[a-zA-Z0-9_]{8,14}\/B[a-zA-Z0-9_]{8,14}\/[a-zA-Z0-9_]{24}/;

export const REGEX_TEAMS_WORKFLOW_URL = /^(https?:\/\/)?([\w-]+\.)+[\w]{2,}(:\d{2,5})?(\/\S*)?$/i;

export const REGEX_PHONE_BASIC_ALLOWED = /[^0-9+()\s\-.]/;
export const REGEX_PHONE_PLUS = /\+/g;
export const REGEX_PHONE_ONLY_PLUS = /^\++$/;
export const REGEX_PHONE_GENERAL = /^[\s()+-]*([0-9][\s()+-]*){6,20}$/;
export const REGEX_PHONE_SEPARATORS = /[\s()\-.]/g;

export const REGEX_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const REGEX_URL = /^(http|https):\/\/(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|\/|\?)*)?$/i;

export const REGEX_PASSWORD_UPPERCASE = /[A-Z]/;
export const REGEX_PASSWORD_LOWERCASE = /[a-z]/;
export const REGEX_PASSWORD_SPECIAL = /[-’/`~!#*$@_%+=.,^&(){}[\]|;:”<>?\\]/;
export const REGEX_PASSWORD_SPECIAL_GLOBAL = /[-’/`~!#*$@_%+=.,^&(){}[\]|;:”<>?\\]/g;

export const REGEX_BASE64_PLUS = /\+/g;
export const REGEX_BASE64_SLASH = /\//g;
export const REGEX_BASE64_PADDING = /=+$/;
export const REGEX_BASE64_PADDING_GLOBAL = /=+$/g;
export const REGEX_BASE64URL_DASH = /-/g;
export const REGEX_BASE64URL_UNDERSCORE = /_/g;

export const REGEX_SUBDOMAIN_ALNUM = /^[a-z0-9]+$/;
export const REGEX_ACCELERATOR_CODE_SANITIZE = /[^A-Za-z0-9]/gi;
export const REGEX_ACCELERATOR_CODE_VALID = /^[A-Za-z0-9]{8,}$/;
export const REGEX_ACCELERATOR_TITLE_TO_SUBDOMAIN = /[^a-z0-9]+/g;

export const REGEX_ENV_SUFFIX_INVALID = /[^a-z0-9-]/gi;
export const REGEX_ENV_SUFFIX_WHITESPACE = /\s/g;

export const REGEX_DOMAIN = /^(?!:\/\/)([a-zA-Z0-9-_]+(\.[a-zA-Z0-9-_]+)+)(:\d+)?(\/.*)?$/;

export const REGEX_SPARK_WORKSPACE_ID = /^[1-9][0-9]*$/;
export const REGEX_SPARK_WORKSPACE_SLUG = /^[a-z][a-z0-9-]{1,98}$/;

export const REGEX_FILENAME_EXTENSION = /^[a-zA-Z0-9]{1,10}$/;
export const REGEX_FILENAME_SAFE = /^[a-zA-Z0-9](?:[a-zA-Z0-9._() -]*[a-zA-Z0-9_) -])?\.[a-zA-Z0-9]{1,10}$/;
export const REGEX_FILENAME_SAFE_PLAIN = /^[a-zA-Z0-9][a-zA-Z0-9._-]*\.[a-zA-Z0-9]{1,10}$/;
export const REGEX_FILENAME_TRAILING_DOT_OR_SPACE = /[. ]$/;
export const REGEX_FILENAME_TRAILING_DOTS_SPACES = /[. ]+$/;
export const REGEX_FILENAME_SANITIZE = /[/\\:*?"<>|#%[\]{}^`]/;
export const REGEX_FILENAME_FORBIDDEN_CHARS = /[/\\:*?"<>|#%[\]{}^`]/;
export const REGEX_FILENAME_FORBIDDEN_CHARS_GLOBAL = /[/\\:*?"<>|#%[\]{}^`]/g;

export const REGEX_OBJECTHELPER_UNESCAPE = /\\(\\)?/g;

/**
 * Used for parsing object property paths.
 * Kept at module scope to avoid recompilation on every call.
 */
export const REGEX_OBJECTHELPER_PROP_NAME = new RegExp(
	'[^.[\\]]+|\\[(?:([^"\'][^[]*)|(["\'])((?:(?!\\2)[^\\\\]|\\\\.)*?)\\2)\\]|(?=(?:\\.|\\[\\])(?:\\.|\\[\\]|$))',
	'g',
);

/**
 * Used for validating IPv4, IPv6 or hostname values in RDP settings.
 */
export const REGEX_RDP_HOST = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$|^(?:[A-F0-9]{1,4}:){7}[A-F0-9]{1,4}$|^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])$/m;

/**
 * Used for IP and CIDR parsing/validation helpers.
 */
export const REGEX_IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
export const REGEX_IPV6 = /^(([0-9a-fA-F]{1,4})(:|::)){1,7}([0-9a-fA-F]{0,4})$/;
export const REGEX_DOUBLE_COLON = /::/g;
export const REGEX_CIDR = /^(.+?)\/(\d{1,3})$/;
export const REGEX_NORMALIZE_CIDR = /^(.+?)\/(\d{1,2})$/;
export const REGEX_IPV4_PARTIAL = /^(\d{1,3})(?:\.(\d{1,3})(?:\.(\d{1,3})(?:\.(\d{1,3}))?)?)?$/;
export const REGEX_HEXTET = /^[0-9a-fA-F]*$/;
export const REGEX_COLON = /:/g;

export const REGEX_CSV_NEWLINE = /\r?\n/g;
export const REGEX_FILENAME = /^[a-zA-Z0-9_\-. ]+$/;
export const REGEX_NEWLINE = /(?:\r\n|\r|\n)/g;
export const REGEX_CUSTOM_DOMAIN = /^((?:(?:(?:\w[.\-+]?)*)\w)+)((?:(?:(?:\w[.\-+]?){0,62})\w)+)\.(\w{2,6})$/;

export const REGEX_CLEAN_STRING = /[^0-9A-Z]+/gi;
